<template>
  <el-form
    :model="formData"
    :rules="formRules"
    ref="subForm"
    label-width="140px"
    style="margin-top: 15px"
  >
    <!-- 廠牌 (OpenAI、Gemini) -->
    <el-form-item label="廠牌" prop="aiBrand">
      <el-select v-model="formData.aiBrand" placeholder="請選擇廠牌" style="width: 100%" filterable>
        <el-option
          v-for="aiBrand in aiBrandOptions"
          :key="aiBrand.value"
          :label="aiBrand.label"
          :value="aiBrand.value"
        />
      </el-select>
    </el-form-item>

    <!-- 專案 -->
    <el-form-item label="專案" prop="project">
      <el-select v-model="formData.project" placeholder="請選擇專案" style="width: 100%" filterable>
        <el-option
          v-for="project in projectOptions"
          :key="project.value"
          :label="project.label"
          :value="project.value"
        />
      </el-select>
    </el-form-item>

    <!-- 功能編號 -->
    <el-form-item label="功能編號" prop="functionCode">
      <el-select
        v-model="formData.functionCode"
        placeholder="請選擇功能編號"
        style="width: 100%"
        filterable
      >
        <el-option
          v-for="code in functionCodeOptions"
          :key="code.value"
          :label="code.label"
          :value="code.value"
        />
      </el-select>
    </el-form-item>

    <!-- 功能名稱 -->
    <el-form-item label="功能名稱" prop="functionName">
      <el-input v-model="formData.functionName" placeholder="請輸入功能名稱" />
    </el-form-item>

    <!-- 模型 -->
    <el-form-item label="模型" prop="useModel">
      <el-select
        v-model="formData.useModel"
        placeholder="請選擇 OpenAI 模型"
        style="width: 100%"
        filterable
      >
        <el-option-group
          v-for="group in filterModelByFunctionCode(formData.functionCode)"
          :key="group.label"
          :label="group.label"
        >
          <el-option
            v-for="model in group.options"
            :key="model.value"
            :label="model.label"
            :value="model.value"
          />
        </el-option-group>
      </el-select>
    </el-form-item>

    <!-- 功能類型多選框 -->
    <el-form-item label="功能類型">
      <el-checkbox-group v-model="formData.functionTypes">
        <el-checkbox label="文字分析"></el-checkbox>
        <el-checkbox label="生成圖片"></el-checkbox>
        <el-checkbox label="影像辨識"></el-checkbox>
      </el-checkbox-group>
    </el-form-item>

    <!-- 提示詞：只要有勾選任何一項就顯示 -->
    <el-form-item v-if="showPromptField" label="提示詞">
      <el-input
        v-model="formData.prompt"
        type="textarea"
        :rows="5"
        placeholder="請輸入 AI Agent 的 提示詞 (Prompt)..."
        resize="vertical"
      />
    </el-form-item>

    <!-- 生成圖片輸出圖片尺寸 (Size) -->
    <el-form-item v-if="formData.functionTypes.includes('生成圖片')" label="輸出圖片尺寸">
      <el-select v-model="formData.outputResolution" placeholder="請選擇尺寸" style="width: 100%">
        <el-option label="1024 × 1024 (正方形 1:1)" value="1024x1024" />
        <el-option label="1792 × 1024 (橫式 16:9)" value="1792x1024" />
        <el-option label="1024 × 1792 (直式 9:16)" value="1024x1792" />
      </el-select>
      <div class="form-tip">※ 依據 OpenAI 官方建議之長寬比例進行輸出。</div>
    </el-form-item>

    <!-- 生成圖片解析度/品質 (Quality) -->
    <el-form-item v-if="formData.functionTypes.includes('生成圖片')" label="圖片生成解析度">
      <el-radio-group v-model="formData.outputImageQuality">
        <el-radio-button label="low">低品質 (Low)</el-radio-button>
        <el-radio-button label="medium">中品質 (Medium)</el-radio-button>
        <el-radio-button label="hd">高品質 (HD)</el-radio-button>
      </el-radio-group>
      <div class="form-tip">※ HD 品質會消耗較多 Token，但畫面細節、材質與光影表現會顯著提升。</div>
    </el-form-item>

    <!-- 影像辨識專屬：辨識採樣率 (DPI) -->
    <el-form-item v-if="formData.functionTypes.includes('影像辨識')" label="辨識採樣率 (DPI)">
      <el-select
        v-model="formData.convertDpi"
        placeholder="請選擇 DPI 轉換規格"
        style="width: 100%"
      >
        <el-option label="72 DPI (螢幕預覽低解析度 - 速度極快)" :value="72" />
        <el-option label="100 DPI (網頁影像輕量化)" :value="100" />
        <el-option label="150 DPI (標準 OCR 文字辨識推薦 - 速度與精準平衡)" :value="150" />
        <el-option label="200 DPI (清晰文件與傳真)" :value="200" />
        <el-option label="300 DPI (高精細印刷品 / 複雜表格 / 手寫字辨識)" :value="300" />
        <el-option label="400 DPI (細微線條與小字特殊辨識)" :value="400" />
        <el-option label="600 DPI (醫療影像 / 高端檔案數位化存檔)" :value="600" />
        <el-option label="1000 DPI (超高解析度精密採樣 - 轉圖耗時較長)" :value="1000" />
      </el-select>
      <div class="form-tip">※ 控制後端將 PDF/影像轉圖至 AI 辨識時的像素採樣密度 (DPI)。</div>
    </el-form-item>
  </el-form>
</template>

<script>
import { getOpenAIModelOptions } from '@/api/openAI'

export default {
  name: 'OpenAiForm',
  props: {
    formData: {
      type: Object,
      required: true,
      default: () => ({}),
    },
  },
  data() {
    return {
      formRules: {
        aiBrand: [{ required: true, message: '請選擇廠牌名稱', trigger: 'blur' }],
        project: [{ required: true, message: '請選擇專案名稱', trigger: 'blur' }],
        functionCode: [{ required: true, message: '請選擇功能編號', trigger: 'blur' }],
        functionName: [{ required: true, message: '請輸入功能名稱', trigger: 'blur' }],
      },
      aiBrandOptions: [
        {
          label: 'OpenAI',
          value: 'OpenAI',
        },
      ],
      projectOptions: [
        {
          label: '3GTAPP後台',
          value: 'ita.GroupTravel.Agency.API',
        },
      ],
      functionCodeOptions: [
        {
          label: '解析飛機航班編號生成航空名稱 (GetFlightInfoAsync)',
          value: 'GetFlightInfoAsync',
        },
        {
          label: '天氣描述生成穿搭圖片 (WeatherOutfitImage)',
          value: 'GenerateAndUploadS3WeatherOutfitImageAsync',
        },
        {
          label: '遊行程描述生成行程圖片 (TourLtineraryImage)',
          value: 'GenerateAndUploadS3TourLtineraryImageAsync',
        },
        { label: '解析旅客設定資料 (ExtractTravelers)', value: 'ExtractTravelersAsync' },
        { label: '解析景點資料 (ExtractLocationsAsync)', value: 'ExtractLocationsAsync' },
        {
          label: '解析PDF旅遊團及旅遊行程資料 (ExtractTravelPdfAsync)',
          value: 'ExtractTravelPdfAsync',
        },
        {
          label: '生成旅遊團分析報告 (GenerateTravelAnalysisReportToPdf)',
          value: 'GenerateTravelAnalysisReportToPdf',
        },
        {
          label: 'OCR辨識護照轉成旅客資料 (OcrPassportToTraveler)',
          value: 'OcrPassportToTraveler',
        },
        {
          label: 'AI虛擬客服 (AIChatForQA)',
          value: 'AIChatForQA',
        },
        {
          label: '生成包團報價單 (GenerateAgencyTravelGroupQuoteContentByAI)',
          value: 'GenerateAgencyTravelGroupQuoteContentByAI',
        },
      ],
      modelOptions: [],
    }
  },
  created() {
    this.fetchRemoteModels()
  },
  methods: {
    async fetchRemoteModels() {
      try {
        const vm = this
        vm.$emit('set-loading', true)
        const response = await getOpenAIModelOptions()

        if (response.data && response.success) {
          vm.modelOptions = response.data
        } else {
          vm.loadLocalBackupModels()
        }

        vm.$emit('set-loading', false)
      } catch (error) {
        console.error('無法取得遠端模型清單，啟用備援清單', error)
        vm.loadLocalBackupModels()
        vm.$emit('set-loading', false)
      }
    },

    loadLocalBackupModels() {
      this.modelOptions = [
        {
          label: '推理系列 (Reasoning)',
          options: [
            {
              label: 'o1',
              value: 'o1',
            },
            {
              label: 'o3-mini',
              value: 'o3-mini',
            },
            {
              label: 'o1-pro',
              value: 'o1-pro',
            },
            {
              label: 'o3',
              value: 'o3',
            },
          ],
        },
        {
          label: 'GPT-5 系列',
          options: [
            {
              label: 'gpt-5-chat-latest',
              value: 'gpt-5-chat-latest',
            },
            {
              label: 'gpt-5',
              value: 'gpt-5',
            },
            {
              label: 'gpt-5-mini',
              value: 'gpt-5-mini',
            },
            {
              label: 'gpt-5-nano',
              value: 'gpt-5-nano',
            },
            {
              label: 'gpt-5-codex',
              value: 'gpt-5-codex',
            },
            {
              label: 'gpt-5-pro',
              value: 'gpt-5-pro',
            },
            {
              label: 'gpt-5-search-api',
              value: 'gpt-5-search-api',
            },
            {
              label: 'gpt-5.1-chat-latest',
              value: 'gpt-5.1-chat-latest',
            },
            {
              label: 'gpt-5.1',
              value: 'gpt-5.1',
            },
            {
              label: 'gpt-5.1-codex',
              value: 'gpt-5.1-codex',
            },
            {
              label: 'gpt-5.1-codex-mini',
              value: 'gpt-5.1-codex-mini',
            },
            {
              label: 'gpt-5.1-codex-max',
              value: 'gpt-5.1-codex-max',
            },
            {
              label: 'gpt-5.2',
              value: 'gpt-5.2',
            },
            {
              label: 'gpt-5.2-pro',
              value: 'gpt-5.2-pro',
            },
            {
              label: 'gpt-5.2-chat-latest',
              value: 'gpt-5.2-chat-latest',
            },
            {
              label: 'gpt-5.2-codex',
              value: 'gpt-5.2-codex',
            },
            {
              label: 'gpt-5.3-codex',
              value: 'gpt-5.3-codex',
            },
            {
              label: 'gpt-5.3-chat-latest',
              value: 'gpt-5.3-chat-latest',
            },
            {
              label: 'gpt-5.4-pro',
              value: 'gpt-5.4-pro',
            },
            {
              label: 'gpt-5.4',
              value: 'gpt-5.4',
            },
            {
              label: 'gpt-5.4-nano',
              value: 'gpt-5.4-nano',
            },
            {
              label: 'gpt-5.4-mini',
              value: 'gpt-5.4-mini',
            },
            {
              label: 'gpt-5.5',
              value: 'gpt-5.5',
            },
            {
              label: 'gpt-5.5-pro',
              value: 'gpt-5.5-pro',
            },
          ],
        },
        {
          label: 'GPT-4o 系列',
          options: [
            {
              label: 'gpt-4o',
              value: 'gpt-4o',
            },
            {
              label: 'gpt-4o-mini',
              value: 'gpt-4o-mini',
            },
            {
              label: 'gpt-4o-mini-search-preview',
              value: 'gpt-4o-mini-search-preview',
            },
            {
              label: 'gpt-4o-transcribe',
              value: 'gpt-4o-transcribe',
            },
            {
              label: 'gpt-4o-mini-transcribe',
              value: 'gpt-4o-mini-transcribe',
            },
            {
              label: 'gpt-4o-transcribe-diarize',
              value: 'gpt-4o-transcribe-diarize',
            },
            {
              label: 'gpt-4o-search-preview',
              value: 'gpt-4o-search-preview',
            },
          ],
        },
        {
          label: 'GPT-4 系列',
          options: [
            {
              label: 'gpt-4',
              value: 'gpt-4',
            },
            {
              label: 'gpt-4-turbo',
              value: 'gpt-4-turbo',
            },
            {
              label: 'gpt-4.1',
              value: 'gpt-4.1',
            },
            {
              label: 'gpt-4.1-mini',
              value: 'gpt-4.1-mini',
            },
            {
              label: 'gpt-4.1-nano',
              value: 'gpt-4.1-nano',
            },
          ],
        },
        {
          label: 'GPT-3.5系列',
          options: [
            {
              label: 'gpt-3.5-turbo',
              value: 'gpt-3.5-turbo',
            },
            {
              label: 'gpt-3.5-turbo-16k',
              value: 'gpt-3.5-turbo-16k',
            },
            {
              label: 'gpt-3.5-turbo-instruct',
              value: 'gpt-3.5-turbo-instruct',
            },
          ],
        },
        {
          label: '圖片生成系列',
          options: [
            {
              label: 'gpt-image-1',
              value: 'gpt-image-1',
            },
            {
              label: 'gpt-image-1-mini',
              value: 'gpt-image-1-mini',
            },
            {
              label: 'gpt-image-1.5',
              value: 'gpt-image-1.5',
            },
            {
              label: 'chatgpt-image-latest',
              value: 'chatgpt-image-latest',
            },
            {
              label: 'gpt-image-2',
              value: 'gpt-image-2',
            },
          ],
        },
      ]
    },

    filterModelByFunctionCode(key) {
      const vm = this

      switch (key) {
        case 'GenerateAndUploadS3WeatherOutfitImageAsync':
        case 'GenerateAndUploadS3TourLtineraryImageAsync':
          const newOptions = vm.modelOptions.filter(s => s.label == '圖片生成系列')
          const isHave =
            newOptions.filter(
              s => s.options.filter(a => a.label == this.formData.useModel).length > 0
            ).length > 0

          if (!isHave) {
            this.formData.useModel = newOptions[0].options[0].value
          }

          return newOptions
        default:
          return vm.modelOptions
      }
    },
    validateForm() {
      return new Promise(resolve => {
        this.$refs['subForm'].validate(valid => {
          resolve(valid) // 驗證成功回傳 true，失敗回傳 false
        })
      })
    },
  },
  computed: {
    showPromptField() {
      const types = this.formData.functionTypes || []
      return types.includes('文字分析') || types.includes('生成圖片') || types.includes('影像辨識')
    },
    isEditMode() {
      return !!(this.formData && this.formData._isPersisted)
    },
  },
}
</script>

<style scoped>
.form-tip {
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
  margin-top: 4px;
}
</style>
